'use client';

import React, { useState, useEffect, useRef } from 'react';

// ─── SVG Icon Components ───────────────────────────────────────────────────
const CheckIcon = ({ className = '' }: { className?: string }) => (
  <svg width="13" height="13" viewBox="0 0 12 12" fill="none" aria-hidden="true" className={className}>
    <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const XIcon = ({ className = '' }: { className?: string }) => (
  <svg width="13" height="13" viewBox="0 0 12 12" fill="none" aria-hidden="true" className={className}>
    <path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

const PhoneIcon = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.1 19.79 19.79 0 0 1 1.61 4.54 2 2 0 0 1 3.59 2.36h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.18 6.18l1.91-1.91a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const PlusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path d="M7 3v8M3 7h8" stroke="#6B7280" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const MinusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path d="M3 7h8" stroke="#2F6BFF" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

// ─── FAQ Item Component ────────────────────────────────────────────────────
interface FaqItemProps {
  question: string;
  answer: string;
  index: number;
}

const FaqItem = ({ question, answer, index }: FaqItemProps) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="faq-item">
      <button
        className="w-full flex items-center justify-between py-5 text-left gap-4 hover:text-navy transition-colors"
        aria-expanded={open}
        aria-controls={`faq-answer-${index}`}
        onClick={() => setOpen(!open)}
      >
        <span className="text-base font-semibold text-brand-fg pr-4">{question}</span>
        <span
          className="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center transition-transform duration-300"
          style={{ background: '#F5F7FB' }}
          aria-hidden="true"
        >
          {open ? <MinusIcon /> : <PlusIcon />}
        </span>
      </button>
      <div
        id={`faq-answer-${index}`}
        className={`faq-answer${open ? ' open' : ''}`}
      >
        <p className="text-brand-body text-sm leading-relaxed pb-5 pr-10">{answer}</p>
      </div>
    </div>
  );
};

// ─── Main Page Component ───────────────────────────────────────────────────
export default function BarberShopLandingPage() {
  const [stickyCTAVisible, setStickyCTAVisible] = useState(false);
  const [currentYear, setCurrentYear] = useState<number | null>(null);
  const revealRefs = useRef<HTMLElement[]>([]);

  useEffect(() => {
    setCurrentYear(new Date().getFullYear());
    // Sticky CTA on scroll
    const handleScroll = () => {
      setStickyCTAVisible(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Intersection Observer for reveal animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );

    const elements = document.querySelectorAll('.reveal-hidden');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const faqData = [
    {
      question: 'Can I accept tips through ChargeAct?',
      answer:
        'Yes. ChargeAct makes tip acceptance seamless — clients can add a tip at checkout on a terminal, mobile app, or even online. Tips are tracked separately so you always know exactly what you earned.',
    },
    {
      question: 'Can I take payments in the chair without a full POS setup?',
      answer:
        'Absolutely. You can use your phone with the ChargeAct mobile app and start accepting payments right away — no bulky hardware required. A compact card reader or terminal is also available if you prefer.',
    },
    {
      question: 'Do I need new hardware?',
      answer:
        'Not necessarily. You can use your phone with an app and be up and running without any new equipment. That said, terminals are programmed to the specific bank your merchant account sits at, so existing equipment may not be compatible. Your dedicated rep will walk you through the best option.',
    },
    {
      question: 'How fast can I get approved?',
      answer:
        "Same-day approval is possible as long as all information is correctly provided. We typically say 1–2 days to be safe. The application takes about 5–10 minutes, and once reviewed, your dedicated rep walks you through the setup.",
    },
    {
      question: 'Is there a long-term contract?',
      answer:
        "No long-term contracts. ChargeAct is a month-to-month arrangement. The goal is to earn your business by actually performing better than your current setup — not by locking you in. If it's not working, you're not stuck.",
    },
  ];

  return (
    <>
      {/* ─── HEADER ─────────────────────────────────────────────────────── */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-brand-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5 flex-shrink-0">
            <img
              src="/assets/images/CHARGEACT_text_logo-1775097726221.png"
              alt="ChargeAct logo"
              className="h-8 w-auto"
            />
          </a>
          <div className="flex items-center gap-3 sm:gap-4">
            <a
              href="tel:+13237840656"
              className="hidden sm:flex items-center gap-1.5 text-sm font-semibold text-brand-body hover:text-navy transition-colors"
            >
              <PhoneIcon size={14} />
+1 (323) 784-0656
            </a>
            <a
              href="/apply"
              className="btn-primary-dark px-4 py-2 text-sm hidden sm:inline-flex items-center gap-1.5"
            >
              Apply Now
            </a>
            <a
              href="tel:+13237840656"
              className="sm:hidden flex items-center justify-center w-9 h-9 rounded-lg bg-accent-pale text-accent"
              aria-label="Call ChargeAct"
            >
              <PhoneIcon size={16} />
            </a>
          </div>
        </div>
      </header>

      <main>
        {/* ─── HERO SECTION ──────────────────────────────────────────────── */}
        <section
          className="relative min-h-screen flex items-center pt-16 overflow-hidden noise-overlay"
          style={{ background: 'linear-gradient(135deg, #041f4d 0%, #0A3D91 50%, #2F6BFF 100%)' }}
          aria-label="Hero"
        >
          {/* Grid overlay */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage:
                'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
              backgroundSize: '60px 60px',
            }}
            aria-hidden="true"
          />
          {/* Glow orbs */}
          <div
            className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(47,107,255,0.25) 0%, transparent 70%)' }}
            aria-hidden="true"
          />
          <div
            className="absolute bottom-1/4 left-1/3 w-64 h-64 rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(47,107,255,0.15) 0%, transparent 70%)' }}
            aria-hidden="true"
          />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-24 w-full relative z-10">
            <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
              {/* Left Column */}
              <div className="lg:col-span-6 xl:col-span-5">
                <div
                  className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full"
                  style={{ background: 'rgba(47,107,255,0.2)', border: '1px solid rgba(47,107,255,0.4)' }}
                >
                  <span className="pulse-dot w-1.5 h-1.5 rounded-full bg-accent-light flex-shrink-0" />
                  <span className="text-xs font-semibold text-accent-light tracking-widest uppercase">
                    ✂️ For Barbershops & Barbers
                  </span>
                </div>

                <h1 className="font-display text-4xl sm:text-5xl lg:text-5xl xl:text-6xl text-white leading-tight tracking-tight mb-6">
                  Is Your Processing Costing You{' '}
                  <span className="text-accent-light">Thousands?</span>
                </h1>

                <p className="text-lg sm:text-xl text-white/75 font-normal leading-relaxed mb-8 max-w-xl">
                  Accept credit cards, tips, and digital payments — without high fees or limitations.
                </p>

                <div className="flex flex-col sm:flex-row gap-3 mb-8" id="get-started">
                  <a
                    href="/apply"
                    className="btn-primary px-6 py-3.5 text-base font-bold text-center"
                  >
                    Apply Now
                  </a>
                  <a
                    href="tel:+13237840656"
                    className="btn-secondary px-6 py-3.5 text-base font-semibold text-center"
                  >
                    See How Much You Could Save
                  </a>
                </div>

                <ul className="space-y-2.5">
                  {[
                    'Accept credit cards in the chair or at checkout',
                    'Seamless tip collection — every time',
                    'Next and Same Day Funding Available',
                    'Transparent pricing, no hidden fees',
                    'Real human USA-based support',
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-2.5 text-sm text-white/80 font-medium">
                      <span
                        className="flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center"
                        style={{ background: 'rgba(47,107,255,0.3)' }}
                      >
                        <svg width="9" height="9" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                          <path d="M2 6l3 3 5-5" stroke="#5B8BFF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Right Column — Hero Card */}
              <div className="lg:col-span-6 xl:col-span-7 flex justify-center lg:justify-end">
                <div className="relative w-full max-w-md lg:max-w-lg xl:max-w-xl">
                  {/* Main invoice card */}
                  <div className="glass-card rounded-2xl p-6 mb-4 shadow-2xl">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-xs font-semibold text-white/50 uppercase tracking-widest mb-1">Payment Receipt</p>
                        <p className="text-white font-bold text-lg">Example: Haircut + Fade + Tip</p>
                        <p className="text-white/60 text-sm">Completed · Tracked · Deposited</p>
                      </div>
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center"
                        style={{ background: 'rgba(47,107,255,0.25)' }}
                      >
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <rect x="2" y="5" width="20" height="14" rx="2" />
                          <line x1="2" y1="10" x2="22" y2="10" />
                        </svg>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div className="rounded-xl p-3 text-center" style={{ background: 'rgba(255,255,255,0.06)' }}>
                        <p className="text-white/50 text-xs mb-1">Service</p>
                        <p className="font-bold text-sm text-white">$45.00</p>
                      </div>
                      <div className="rounded-xl p-3 text-center" style={{ background: 'rgba(255,255,255,0.06)' }}>
                        <p className="text-white/50 text-xs mb-1">Processing Fee</p>
                        <p className="font-bold text-sm text-white">$0.72</p>
                        <p className="text-white/40 text-xs mt-0.5">ChargeAct rate</p>
                      </div>
                      <div className="rounded-xl p-3 text-center" style={{ background: 'rgba(255,255,255,0.06)' }}>
                        <p className="text-white/50 text-xs mb-1">You Keep</p>
                        <p className="font-bold text-sm text-green-400">$44.28</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-white/50">vs. avg competitors: $1.58 fee · You keep $43.42</span>
                      <span className="text-xs font-bold text-green-400 bg-green-400/10 px-2 py-0.5 rounded-full">Save $0.86<sup className="text-[8px] font-bold align-super ml-0.5">1</sup></span>
                    </div>
                  </div>

                  {/* Stat cards */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="glass-card rounded-xl p-4 float-card-1">
                      <div className="flex items-center gap-1.5 mb-2">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-red-400" aria-hidden="true">
                          <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                        </svg>
                        <span className="text-white/50 text-xs font-medium truncate">Annual Fee Bleed</span>
                      </div>
                      <p className="font-extrabold text-sm leading-tight text-red-400">$3,200+<sup className="text-[8px] font-bold align-super ml-0.5">1</sup></p>
                      <p className="text-white/40 text-xs mt-1 leading-tight">on avg processors at typical volume</p>
                    </div>
                    <div className="glass-card rounded-xl p-4 float-card-2">
                      <div className="flex items-center gap-1.5 mb-2">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-400" aria-hidden="true">
                          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                        </svg>
                        <span className="text-white/50 text-xs font-medium truncate">Tips Tracked</span>
                      </div>
                      <p className="font-extrabold text-sm leading-tight text-green-400">100% Accurate</p>
                      <span className="inline-block mt-1 text-xs font-bold text-green-400 bg-green-400/15 px-1.5 py-0.5 rounded-full">✓ Compliant</span>
                    </div>
                    <div className="glass-card rounded-xl p-4 float-card-3">
                      <div className="flex items-center gap-1.5 mb-2">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-accent-light" aria-hidden="true">
                          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
                          <polyline points="17 6 23 6 23 12" />
                        </svg>
                        <span className="text-white/50 text-xs font-medium truncate">Rate Reduced</span>
                      </div>
                      <p className="font-extrabold text-sm leading-tight text-white">Saving $180/mo<sup className="text-[9px] font-bold text-accent-light align-super ml-0.5">1</sup></p>
                      <p className="text-white/40 text-xs mt-1 leading-tight">vs. flat-rate on same volume</p>
                    </div>
                  </div>

                  {/* Approval badge */}
                  <div className="glass-card rounded-xl p-4 mt-3 flex items-center gap-3 float-card-2">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: 'rgba(16,185,129,0.2)' }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-white font-bold text-sm">Account Approved: As Early As Same Day</p>
                      <p className="text-white/50 text-xs">Dedicated rep assigned · Ready to process</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ─── PROBLEM SECTION ───────────────────────────────────────────── */}
        <section id="problem" className="py-20 lg:py-28" style={{ background: '#F5F7FB' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center max-w-3xl mx-auto mb-14 reveal-hidden">
              <p className="eyebrow mb-3">The Payment Stack Problem</p>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-brand-fg tracking-tight leading-tight mb-4">
                Most Barbers Are Using the Wrong Payment Tools
              </h2>
              <p className="text-lg text-brand-body leading-relaxed">
                Payment processing apps were built for splitting dinner — not running a barbershop. Every time you use them, you're leaving money on the table and looking less professional.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              {/* Bad card */}
              <div className="comparison-card reveal-hidden stagger-1">
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-5">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: '#FEF2F2', color: '#EF4444' }}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-bold text-brand-fg text-base">Payment Processing Apps</h3>
                      <p className="text-brand-muted text-xs">Personal payment apps</p>
                    </div>
                  </div>
                  <div className="space-y-3 mb-5">
                    {[
                      { label: 'Professionalism', value: 'Looks like a side hustle' },
                      { label: 'Tip tracking', value: 'No record at all' },
                      { label: 'Dispute support', value: 'No protection at all' },
                      { label: 'Dedicated rep', value: 'No human contact' },
                      { label: 'Pricing transparency', value: 'Varies / hidden' },
                    ].map(({ label, value }) => (
                      <div key={label} className="flex flex-col gap-0.5">
                        <span className="text-xs font-semibold text-brand-muted uppercase tracking-wider">{label}</span>
                        <div className="flex items-start gap-1.5">
                          <span className="flex-shrink-0 mt-0.5 text-red-400"><XIcon /></span>
                          <span className="text-sm text-brand-body">{value}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="rounded-xl px-4 py-2.5 text-center" style={{ background: '#FEF2F2' }}>
                    <span className="text-sm font-bold text-red-500">Not built for business</span>
                  </div>
                </div>
              </div>

              {/* Good card */}
              <div className="comparison-card featured reveal-hidden stagger-2">
                <div className="bg-accent text-white text-xs font-bold tracking-widest uppercase text-center py-2 rounded-t-2xl">
                  ✓ Recommended for Barbers
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-5">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: '#2F6BFF', color: '#fff' }}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M3 9L7.5 4.5L12 9L7.5 13.5L3 9Z" fill="white" fillOpacity="0.65" />
                        <path d="M7.5 9L12 4.5L16.5 9L12 13.5L7.5 9Z" fill="white" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-bold text-brand-fg text-base">ChargeAct</h3>
                      <p className="text-brand-muted text-xs">Built for barbershops</p>
                    </div>
                  </div>
                  <div className="space-y-3 mb-5">
                    {[
                      { label: 'Professionalism', value: 'Digital receipts + payment tracking' },
                      { label: 'Tip tracking', value: 'Built-in tip collection & reporting' },
                      { label: 'Dispute support', value: 'Dedicated rep + chargeback protection' },
                      { label: 'Dedicated rep', value: 'Named contact, real phone number' },
                      { label: 'Pricing transparency', value: 'Competitive rates — no guessing' },
                    ].map(({ label, value }) => (
                      <div key={label} className="flex flex-col gap-0.5">
                        <span className="text-xs font-semibold text-brand-muted uppercase tracking-wider">{label}</span>
                        <div className="flex items-start gap-1.5">
                          <span className="flex-shrink-0 mt-0.5 text-green-500"><CheckIcon /></span>
                          <span className="text-sm text-brand-fg font-medium">{value}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="rounded-xl px-4 py-2.5 text-center" style={{ background: '#EBF1FF' }}>
                    <span className="text-sm font-bold text-accent">Built to protect your revenue</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-10 text-center reveal-hidden">
              <p className="text-brand-body text-base max-w-2xl mx-auto">
                <strong className="text-brand-fg">If a client wants to pay with a card and you can't accept it, you risk losing the sale</strong>{' '}
                — and looking unprofessional in the process.
              </p>
            </div>
          </div>
        </section>

        {/* ─── SOLUTION / FEATURES SECTION ───────────────────────────────── */}
        <section id="margins" className="py-20 lg:py-28 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center max-w-2xl mx-auto mb-14 reveal-hidden">
              <p className="eyebrow mb-3">A Better Way to Accept Payments</p>
              <h2 className="font-display text-3xl sm:text-4xl text-brand-fg tracking-tight leading-tight">
                Four ways your current setup is costing you money right now
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              {[
                {
                  icon: (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <rect x="2" y="5" width="20" height="14" rx="2" ry="2" />
                      <line x1="2" y1="10" x2="22" y2="10" />
                    </svg>
                  ),
                  title: 'Accept All Major Credit Cards',
                  body: 'Visa, Mastercard, Amex, Discover — accept them all. Clients who only carry cards won\'t have to leave to find an ATM. More payment options means more completed cuts and higher average tickets.',
                },
                {
                  icon: (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                    </svg>
                  ),
                  title: 'Accept Tips the Right Way',
                  body: 'Stop relying on clients to have cash for tips. ChargeAct lets clients add a tip at checkout — on a terminal, mobile app, or online. Tips are tracked and deposited directly to your account.',
                },
                {
                  icon: (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
                      <line x1="12" y1="18" x2="12.01" y2="18" />
                    </svg>
                  ),
                  title: 'Take Payments In-Chair or On Mobile',
                  body: 'Whether you\'re at your station, working a pop-up, or doing house calls — ChargeAct works anywhere. Use your phone, a compact card reader, or a countertop terminal. Your shop, your setup.',
                },
                {
                  icon: (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                  ),
                  title: 'Accept ACH, Bank Payments & Online',
                  body: 'Let clients pay via ACH bank transfer or online before their appointment. Perfect for booth renters collecting from shop owners, or clients who prefer to pay ahead. Flexible for every situation.',
                },
              ].map(({ icon, title, body }, i) => (
                <div
                  key={title}
                  className={`rounded-2xl p-6 reveal-hidden stagger-${i + 1}`}
                  style={{ background: '#F5F7FB', border: '1.5px solid #E5E7EB' }}
                >
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4 text-accent" style={{ background: '#EBF1FF' }}>
                    {icon}
                  </div>
                  <h3 className="font-bold text-brand-fg text-lg mb-2">{title}</h3>
                  <p className="text-brand-body text-sm leading-relaxed">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ─── FEE MATH / SAVINGS TABLE ──────────────────────────────────── */}
        <section id="fee-math" className="py-20 lg:py-28" style={{ background: '#F5F7FB' }}>
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center max-w-2xl mx-auto mb-12 reveal-hidden">
              <p className="eyebrow mb-3">The Fee Math</p>
              <h2 className="font-display text-3xl sm:text-4xl text-brand-fg tracking-tight leading-tight mb-4">
                The fee math looks small until you run the numbers on every cut.
              </h2>
              <p className="text-brand-body text-base">
                Here&apos;s what a difference in processing could mean for your barbershop with ChargeAct pricing.
              </p>
            </div>

            <div className="rounded-2xl overflow-hidden shadow-card reveal-hidden" style={{ border: '1.5px solid #E5E7EB' }}>
              <div className="fee-table-header grid grid-cols-4 gap-0">
                <div className="px-5 py-4 text-xs font-bold uppercase tracking-widest text-white/90">Service Scenario</div>
                <div className="px-5 py-4 text-xs font-bold uppercase tracking-widest text-white/90 text-center">Avg Processing</div>
                <div className="px-5 py-4 text-xs font-bold uppercase tracking-widest text-white/90 text-center">ChargeAct</div>
                <div className="px-5 py-4 text-xs font-bold uppercase tracking-widest text-white/90 text-center">You Save<sup className="text-[8px] font-bold align-super ml-0.5">1</sup></div>
              </div>
              {[
                { scenario: '$35 haircut', avg: '$1.08', ca: '$0.63', save: '$0.45', bg: 'bg-white' },
                { scenario: '$55 cut + beard trim', avg: '$1.70', ca: '$0.99', save: '$0.71', bg: 'bg-brand-secondary' },
                { scenario: '$80 cut + color + tip', avg: '$2.48', ca: '$1.44', save: '$1.04', bg: 'bg-white' },
              ].map(({ scenario, avg, ca, save, bg }) => (
                <div key={scenario} className={`grid grid-cols-4 gap-0 ${bg}`}>
                  <div className="px-5 py-4 text-sm font-semibold text-brand-fg">{scenario}</div>
                  <div className="px-5 py-4 text-sm text-center text-red-500 font-semibold line-through decoration-red-300">{avg}</div>
                  <div className="px-5 py-4 text-sm text-center text-accent font-bold">{ca}</div>
                  <div className="px-5 py-4 text-sm text-center font-bold text-green-600">{save}<sup className="text-[8px] font-bold align-super ml-0.5">1</sup></div>
                </div>
              ))}
              <div className="grid grid-cols-4 gap-0 bg-accent/5 border-t-2 border-accent/30">
                <div className="px-5 py-4 text-sm font-semibold text-brand-fg font-bold">
                  800 cuts/yr avg $50
                  <span className="block text-xs font-normal text-brand-muted mt-0.5">Annual estimate</span>
                </div>
                <div className="px-5 py-4 text-sm text-center text-red-500 font-semibold line-through decoration-red-300">$1,240/yr</div>
                <div className="px-5 py-4 text-sm text-center text-accent font-bold">$720/yr</div>
                <div className="px-5 py-4 text-sm text-center font-bold text-green-600 text-base">$520/yr<sup className="text-[8px] font-bold align-super ml-0.5">1</sup></div>
              </div>
            </div>

            <p className="text-center text-xs text-brand-muted mt-4 leading-relaxed">
              Rates are subject to merchant history and prices vary between businesses. Actual savings vary. For illustrative purposes only.
            </p>

            <div className="text-center mt-8 reveal-hidden">
              <a
                href="/apply"
                className="btn-primary-dark px-8 py-3.5 text-base font-bold inline-flex items-center gap-2"
              >
                See How Much You&apos;re Overpaying <ArrowRightIcon />
              </a>
            </div>
          </div>
        </section>

        {/* ─── BENEFITS / SOLUTIONS SECTION ──────────────────────────────── */}
        <section id="solutions" className="py-20 lg:py-28" style={{ background: '#F5F7FB' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center max-w-2xl mx-auto mb-14 reveal-hidden">
              <p className="eyebrow mb-3">Built for Barbershops</p>
              <h2 className="font-display text-3xl sm:text-4xl text-brand-fg tracking-tight leading-tight mb-4">
                Built for Barbershops
              </h2>
              <p className="text-brand-body text-base">
                Seven ways ChargeAct helps barbers keep more of what they earn, look more professional, and run a tighter operation.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
                {
                  icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>,
                  title: 'Keep More of What You Earn',
                  body: 'Competitive rates designed for barbershops mean you keep more of every dollar. Even a small reduction in processing fees adds up to hundreds of dollars back in your pocket each year.',
                },
                {
                  icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="2" y="5" width="20" height="14" rx="2" /><line x1="2" y1="10" x2="22" y2="10" /></svg>,
                  title: 'Accept Tips the Right Way',
                  body: 'Clients can add a tip at checkout — no awkward cash moments. Tips are tracked, reported, and deposited automatically. Your clients will appreciate the smooth experience.',
                },
                {
                  icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>,
                  title: 'Look More Professional to Clients',
                  body: 'Digital receipts, clean checkout flows, and real payment infrastructure signal that you run a real business — not a side hustle. Clients notice and trust you more.',
                },
                {
                  icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="5" y="2" width="14" height="20" rx="2" ry="2" /><line x1="12" y1="18" x2="12.01" y2="18" /></svg>,
                  title: 'Take Payments in the Chair or at Checkout',
                  body: 'Charge clients right at your station with a mobile reader, or at a countertop terminal. Works for walk-ins, appointments, and booth renters alike.',
                },
                {
                  icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>,
                  title: 'Handle High Client Volume Easily',
                  body: 'Fast checkout means shorter wait times and more clients served. No fumbling with apps or waiting for transfers — just tap, pay, done.',
                },
                {
                  icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.1 19.79 19.79 0 0 1 1.61 4.54 2 2 0 0 1 3.59 2.36h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.18 6.18l1.91-1.91a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" /></svg>,
                  title: 'Reduce Risk with Chargeback Protection',
                  body: 'Every transaction is documented. If a client disputes a charge, your dedicated rep steps in with the evidence and support you need to win — not leave you hanging.',
                },
                {
                  icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.1 19.79 19.79 0 0 1 1.61 4.54 2 2 0 0 1 3.59 2.36h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.18 6.18l1.91-1.91a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" /></svg>,
                  title: 'Dedicated Account Manager for Support',
                  body: 'You get a named contact with a real phone number. Not a chatbot. Not a ticket system. A real person who knows your account and picks up when you call.',
                },
              ].map(({ icon, title, body }, i) => (
                <div key={title} className={`solution-card p-6 reveal-hidden stagger-${(i % 6) + 1}`}>
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 text-accent" style={{ background: '#EBF1FF' }}>
                    {icon}
                  </div>
                  <h3 className="font-bold text-brand-fg text-base mb-2">{title}</h3>
                  <p className="text-brand-body text-sm leading-relaxed">{body}</p>
                </div>
              ))}
            </div>

            <div className="text-center mt-12 reveal-hidden">
              <a
                href="/apply"
                className="btn-primary-dark px-8 py-3.5 text-base font-bold inline-flex items-center gap-2"
              >
                Apply Now <ArrowRightIcon />
              </a>
            </div>
          </div>
        </section>

        {/* ─── HOW IT WORKS ──────────────────────────────────────────────── */}
        <section id="how-it-works" className="py-20 lg:py-28 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center max-w-2xl mx-auto mb-14 reveal-hidden">
              <p className="eyebrow mb-3">Simple Process</p>
              <h2 className="font-display text-3xl sm:text-4xl text-brand-fg tracking-tight leading-tight mb-4">
                Up and running faster than you think
              </h2>
              <p className="text-brand-body text-base">
                Switching payment processors sounds painful. It isn&apos;t. Most barbershops are processing with ChargeAct within 24–48 hours of applying.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  num: '01',
                  icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M21 21l-6-6a4 4 0 0 0-5.65 0L5 21" /></svg>,
                  title: 'Apply Online',
                  body: 'Short application — shop info, processing volume, current setup. No long forms, no guesswork. Takes about 5 minutes.',
                },
                {
                  num: '02',
                  icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>,
                  title: 'Get Approved',
                  body: 'A dedicated specialist is assigned to your account, reviews your business, and advises on the right options, equipment, and rates for your shop.',
                },
                {
                  num: '03',
                  icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="22 12 13.5 15.5 8.5 10.5 1 18" /></svg>,
                  title: 'Get Your Setup',
                  body: 'Hardware if needed, mobile app configured, and your dedicated account manager walks you through everything — same-day approval is standard.',
                },
                {
                  num: '04',
                  icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>,
                  title: 'Start Accepting Payments',
                  body: 'Collect in the chair, accept tips, and know every transaction is documented and every dispute has a rep in your corner. That\'s it.',
                },
              ].map(({ num, icon, title, body }, i) => (
                <div
                  key={num}
                  className={`relative rounded-2xl p-6 reveal-hidden stagger-${i + 1}`}
                  style={{ background: '#F5F7FB', border: '1.5px solid #E5E7EB' }}
                >
                  <div className="text-5xl font-extrabold leading-none mb-4" style={{ color: '#E5E7EB', fontVariantNumeric: 'tabular-nums' }}>
                    {num}
                  </div>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3 text-accent" style={{ background: '#EBF1FF' }}>
                    {icon}
                  </div>
                  <h3 className="font-bold text-brand-fg text-base mb-2">{title}</h3>
                  <p className="text-brand-body text-sm leading-relaxed">{body}</p>
                  {i < 3 && (
                    <div
                      className="hidden lg:block absolute top-10 -right-3 w-6 h-0.5 z-10"
                      style={{ background: 'linear-gradient(90deg, #2F6BFF 0%, #E5E7EB 100%)' }}
                      aria-hidden="true"
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="text-center mt-12 flex flex-col sm:flex-row gap-4 justify-center reveal-hidden">
              <a
                href="/apply"
                className="btn-primary-dark px-8 py-3.5 text-base font-bold inline-flex items-center justify-center gap-2"
              >
                Apply Now <ArrowRightIcon />
              </a>
              <a
                href="tel:+13237840656"
                className="btn-outline-dark px-8 py-3.5 text-base font-semibold inline-flex items-center justify-center gap-2"
              >
                <PhoneIcon size={16} /> Talk to a Specialist
              </a>
            </div>
          </div>
        </section>

        {/* ─── USE CASES SECTION ─────────────────────────────────────────── */}
        <section id="use-cases" className="py-16 lg:py-20" style={{ background: '#F5F7FB', borderTop: '1px solid #E5E7EB' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 reveal-hidden">
              <p className="eyebrow mb-3">Works for Every Barbershop</p>
              <h2 className="font-display text-2xl sm:text-3xl text-brand-fg tracking-tight">
                Built for Every Type of Barber
              </h2>
              <p className="text-brand-body text-base mt-3 max-w-xl mx-auto">
                Whether you own a shop, rent a booth, or work solo — ChargeAct fits your workflow.
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-5">
              {[
                { emoji: '✂️', title: 'Haircuts & Fades', desc: 'Fast checkout for every cut' },
                { emoji: '🚶', title: 'Walk-In Clients', desc: 'No appointment? No problem' },
                { emoji: '📅', title: 'Appointment Bookings', desc: 'Pre-pay or pay on arrival' },
                { emoji: '💺', title: 'Booth Renters', desc: 'Independent barbers welcome' },
                { emoji: '💰', title: 'Tips & Add-Ons', desc: 'Collect tips seamlessly' },
                { emoji: '🏪', title: 'Shop Owners', desc: 'Manage multiple chairs' },
                { emoji: '📱', title: 'Mobile Barbers', desc: 'Accept payments anywhere' },
              ].map(({ emoji, title, desc }) => (
                <div key={title} className="integration-badge flex flex-col items-center gap-3 px-8 py-6 w-full sm:w-48 reveal-hidden">
                  <div className="h-10 flex items-center justify-center">
                    <span className="text-3xl">{emoji}</span>
                  </div>
                  <div className="text-center">
                    <p className="font-bold text-brand-fg text-sm">{title}</p>
                    <p className="text-brand-muted text-xs mt-0.5">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ─── SUPPORT SECTION ───────────────────────────────────────────── */}
        <section id="support" className="py-20 lg:py-28 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center max-w-2xl mx-auto mb-14 reveal-hidden">
              <p className="eyebrow mb-3">Real Support for Barbers</p>
              <h2 className="font-display text-3xl sm:text-4xl text-brand-fg tracking-tight leading-tight mb-4">
                Real Support for Barbers
              </h2>
              <p className="text-brand-body text-base">
                You shouldn&apos;t have to figure out payment processing alone. ChargeAct gives you a real person in your corner from day one.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.1 19.79 19.79 0 0 1 1.61 4.54 2 2 0 0 1 3.59 2.36h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.18 6.18l1.91-1.91a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" /></svg>,
                  title: 'Dedicated Account Manager',
                  body: 'A named contact assigned to your account. Real phone number. Real person. Not a ticket system.',
                },
                {
                  icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="22 12 13.5 15.5 8.5 10.5 1 18" /></svg>,
                  title: 'Help with Setup',
                  body: 'Your rep walks you through hardware, app configuration, and your first transactions — so you\'re never left guessing.',
                },
                {
                  icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.1 19.79 19.79 0 0 1 1.61 4.54 2 2 0 0 1 3.59 2.36h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.18 6.18l1.91-1.91a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" /></svg>,
                  title: 'Ongoing Support',
                  body: 'Questions after setup? Your rep is still there. Disputes, rate reviews, new equipment — they handle it all.',
                },
                {
                  icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="2" y="5" width="20" height="14" rx="2" /><line x1="2" y1="10" x2="22" y2="10" /></svg>,
                  title: 'Simple, Easy to-Use Tools',
                  body: 'No complicated dashboards. No confusing reports. Just clean, simple tools that show you what you need to run your shop.',
                },
              ].map(({ icon, title, body }, i) => (
                <div
                  key={title}
                  className={`relative rounded-2xl p-6 reveal-hidden stagger-${i + 1}`}
                  style={{ background: '#F5F7FB', border: '1.5px solid #E5E7EB' }}
                >
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4 text-accent" style={{ background: '#EBF1FF' }}>
                    {icon}
                  </div>
                  <h3 className="font-bold text-brand-fg text-base mb-2">{title}</h3>
                  <p className="text-brand-body text-sm leading-relaxed">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ─── FAQ SECTION ───────────────────────────────────────────────── */}
        <section id="faq" className="py-20 lg:py-28" style={{ background: '#F5F7FB' }}>
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12 reveal-hidden">
              <p className="eyebrow mb-3">Common Questions</p>
              <h2 className="font-display text-3xl sm:text-4xl text-brand-fg tracking-tight leading-tight">
                Questions barbers ask before switching
              </h2>
            </div>
            <div className="space-y-0 reveal-hidden">
              {faqData.map((item, i) => (
                <FaqItem key={i} question={item.question} answer={item.answer} index={i} />
              ))}
            </div>
            <div className="mt-10 text-center reveal-hidden">
              <p className="text-brand-body text-sm mb-4">Have a question not covered here?</p>
              <a
                href="tel:+13237840656"
                className="inline-flex items-center gap-2 text-accent font-semibold text-sm hover:text-navy transition-colors"
              >
                <PhoneIcon size={14} />
Call +1 (323) 784-0656 — a specialist will answer
              </a>
            </div>
          </div>
        </section>

        {/* ─── FINAL CTA SECTION ─────────────────────────────────────────── */}
        <section
          id="final-cta"
          className="py-20 lg:py-28 relative overflow-hidden noise-overlay"
          style={{ background: 'linear-gradient(135deg, #041f4d 0%, #0A3D91 55%, #2F6BFF 100%)' }}
          aria-label="Final call to action"
        >
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage:
                'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
              backgroundSize: '60px 60px',
            }}
            aria-hidden="true"
          />
          <div
            className="absolute top-0 right-1/4 w-96 h-96 rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(47,107,255,0.2) 0%, transparent 70%)' }}
            aria-hidden="true"
          />

          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center relative z-10">
            <div className="flex justify-center mb-6">
              <img
                src="/assets/images/IMG_2176-1775098062874.PNG"
                alt="ChargeAct logo"
                className="h-9 w-auto"
              />
            </div>
            <div
              className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full"
              style={{ background: 'rgba(47,107,255,0.2)', border: '1px solid rgba(47,107,255,0.4)' }}
            >
              <span className="text-xs font-semibold text-accent-light tracking-widest uppercase">
                ✂️ For Barbershops & Barbers
              </span>
            </div>

            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight leading-tight mb-5">
              Upgrade How You Get Paid
            </h2>

            <p className="text-lg text-white/75 leading-relaxed mb-10 max-w-2xl mx-auto">
              Stop relying on payment apps and start running your barbershop like a real business. Fast conversation, no-pressure review, custom recommendations for your volume.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
              <a
                href="/apply"
                className="btn-primary px-8 py-4 text-base font-bold text-center"
              >
                Apply Now
              </a>
              <a
                href="tel:+13237840656"
                className="btn-secondary px-8 py-4 text-base font-semibold text-center"
              >
                See How Much You Could Save
              </a>
            </div>

            <div className="flex flex-col items-center gap-3">
              <a
                href="tel:+13237840656"
                className="flex items-center gap-2 text-white font-bold text-xl hover:text-accent-light transition-colors"
              >
                <PhoneIcon size={20} />
+1 (323) 784-0656
              </a>
              <div className="flex flex-wrap justify-center gap-6 text-xs text-white/60 font-medium">
                {['Fast conversation', 'No-pressure review', 'Custom recommendations', 'No long-term contract'].map((item) => (
                  <span key={item} className="flex items-center gap-1.5">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                      <path d="M2 6l3 3 5-5" stroke="#5B8BFF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* ─── FOOTER ──────────────────────────────────────────────────────── */}
      <footer className="border-t border-brand-border py-8 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img
                src="/assets/images/CHARGEACT_text_logo-1775097726221.png"
                alt="ChargeAct logo"
                className="h-7 w-auto"
              />
            </div>
            <div className="flex items-center gap-6 text-sm font-medium text-brand-muted">
              <a href="#solutions" className="hover:text-navy transition-colors">Solutions</a>
              <a href="#faq" className="hover:text-navy transition-colors">FAQ</a>
              <a href="tel:+13237840656" className="hover:text-navy transition-colors">+1 (323) 784-0656</a>
              <a href="mailto:matthew@joinchargeact.com" className="hover:text-navy transition-colors">Contact</a>
            </div>
            <div className="flex items-center gap-4 text-xs text-brand-muted">
              <a href="/privacy" className="hover:text-navy transition-colors">Privacy</a>
              <a href="/terms" className="hover:text-navy transition-colors">Terms</a>
            </div>
          </div>
          <div className="mt-6 text-center text-xs text-brand-muted">
            © {currentYear ?? ''} ChargeAct. All rights reserved. Barbershop payment processing solutions.
          </div>
          {/* ─── DISCLAIMER ─────────────────────────────────────────────── */}
          <div className="mt-6 pt-6 border-t border-brand-border">
            <p className="text-xs font-bold text-brand-muted mb-2 uppercase tracking-wider">Disclaimer <sup>1</sup></p>
            <p className="text-xs text-brand-muted leading-relaxed">
              <strong>IMPORTANT NOTICE — ILLUSTRATIVE DATA ONLY:</strong> The fee comparison figures, savings estimates, processing rate examples, annual cost projections, and all related financial data presented on this page, including but not limited to the fee savings comparison table, the floating savings indicator, and any referenced dollar amounts, are provided solely for illustrative and informational purposes. Such figures constitute hypothetical examples based on generalized assumptions and do not represent actual, guaranteed, or projected results for any specific merchant. Actual processing rates, fees, and savings will vary materially based on individual merchant history, transaction volume, card mix, chargeback history, business type, applicable interchange categories, and other underwriting factors determined at the sole discretion of the acquiring bank and payment network. Nothing contained herein shall constitute a representation, warranty, or guarantee of any specific rate, fee, or savings outcome. Past performance or illustrative examples are not indicative of future results. All rates and terms are subject to change without notice and are contingent upon merchant approval. ChargeAct makes no representations or warranties, express or implied, regarding the accuracy, completeness, or applicability of the illustrative data to any particular merchant's circumstances.
            </p>
          </div>
        </div>
      </footer>

      {/* ─── STICKY MOBILE CTA ───────────────────────────────────────────── */}
      <div className={`sticky-cta flex bg-navy px-4 py-3 flex-col gap-2 shadow-2xl${stickyCTAVisible ? ' visible' : ''}`}>
        <a href="/apply" className="btn-primary w-full py-3 text-sm text-center font-bold">
Apply Now
        </a>
        <a href="tel:+13237840656" className="text-center text-xs text-white/70 font-medium">
Or call +1 (323) 784-0656 — talk to a specialist now
        </a>
      </div>
    </>
  );
}
